import { useState, useEffect, useCallback, useRef } from 'react';
import { WS_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook for AI Trader WebSocket connection.
 * Follows useHyperliquidPositions.js pattern.
 *
 * Returns real-time challenge data, positions, decisions, and connection status.
 */
export function useAiTrader() {
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | connecting | connected
  const [challenge, setChallenge] = useState(null);
  const [positions, setPositions] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);

  const wsRef = useRef(null);
  const reconnectDelayRef = useRef(3000);
  const maxReconnectDelay = 30000;
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (isConnectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) {
      return;
    }

    isConnectingRef.current = true;
    setConnectionStatus('connecting');

    // In development, Vite runs on 5173+ but backend is on 3000
    const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
    const basePath = '/ai-trader';
    const wsUrl = isDev
      ? (WS_BASE_URL ? `${WS_BASE_URL}${basePath}` : `ws://localhost:${window.location.port}${basePath}`)
      : `${WS_BASE_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`}${basePath}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionStatus('connected');
      reconnectDelayRef.current = 3000;
      isConnectingRef.current = false;
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'initial_state':
            setChallenge(message.data.challenge || null);
            setPositions(message.data.positions || []);
            setDecisions(message.data.decisions || []);
            break;

          case 'position_opened':
            setPositions(prev => [message.data, ...prev]);
            break;

          case 'position_closed':
            setPositions(prev => prev.filter(p =>
              p._id !== message.data._id
            ));
            setClosedTrades(prev => [message.data, ...prev].slice(0, 100));
            break;

          case 'new_decision':
            setDecisions(prev => [message.data, ...prev].slice(0, 50));
            break;

          case 'balance_updated':
            setChallenge(prev => prev ? { ...prev, ...message.data } : message.data);
            break;

          case 'challenge_wiped':
            setChallenge(message.data.newChallenge || null);
            setPositions([]);
            break;

          case 'price_update':
            // Update unrealized PnL on open positions
            if (message.data && message.data.symbol && message.data.price) {
              setPositions(prev => prev.map(pos => {
                if (pos.symbol !== message.data.symbol) return pos;
                const currentPrice = message.data.price;
                let unrealizedPnl;
                if (pos.side === 'long') {
                  unrealizedPnl = pos.size * (currentPrice - pos.entryPrice) / pos.entryPrice;
                } else {
                  unrealizedPnl = pos.size * (pos.entryPrice - currentPrice) / pos.entryPrice;
                }
                const pnlPercent = pos.margin !== 0 ? (unrealizedPnl / pos.margin) * 100 : 0;
                return { ...pos, unrealizedPnl, pnlPercent, _currentPrice: currentPrice };
              }));
            }
            break;

          case 'pong':
            break;

          default:
            break;
        }
      } catch (error) {
        console.error('AI Trader WS parse error:', error);
      }
    };

    ws.onerror = () => {
      isConnectingRef.current = false;
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      wsRef.current = null;
      isConnectingRef.current = false;

      if (shouldReconnectRef.current) {
        const delay = Math.min(reconnectDelayRef.current, maxReconnectDelay);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, maxReconnectDelay);
          connect();
        }, delay);
      }
    };
  }, []);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  // Ping every 30 seconds to keep connection alive
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [connectionStatus]);

  return {
    challenge,
    positions,
    decisions,
    closedTrades,
    connectionStatus,
  };
}
